<script lang="ts">
    import { ModalToken } from "@/modal-manager";
    import type InstagramPostEntity from "@apps/instagram/entities/InstagramPostEntity";
    import { createEventDispatcher, onMount } from "svelte";

    import Icon from "@components/Icon.svelte";
    import DotsIcon from "@components/icons/DotsIcon.svelte";
    import EmblaSlider from "@components/Slider/EmblaSlider.svelte";
    import EmblaSliderDots from "@components/Slider/EmblaSliderDots.svelte";
    import SliderArrows from "@components/Slider/SliderArrows.svelte";
    import type { EmblaSliderApi } from "@components/Slider/types";

    import InstagramPost from "../components/InstagramPost.svelte";
    import InstagramPostSkeleton from "../components/InstagramPostSkeleton.svelte";
    import {
        DISPLAY_MODE_HOTSPOT_TYPE,
        DISPLAY_MODE_SHOP_IT_TYPE,
    } from "../constants";

    export let posts: InstagramPostEntity[];
    export let postModalToken: ModalToken<{ id: number }>;
    export let perPage: number;
    export let mobilePerPage: number;
    export let displayMode:
        | typeof DISPLAY_MODE_SHOP_IT_TYPE
        | typeof DISPLAY_MODE_HOTSPOT_TYPE
        | null;
    export let skeleton = false;
    export let skeletonPostsCount = 6;
    export let loop = false;
    export let gutter = 0;
    export let highQualityEnabled: boolean;
    export let autoPlayIntervalForSlider: number;

    let emblaApi: EmblaSliderApi | null = null;
    let effectivePerPage = 5;
    let currentIndex = 0;

    function calculatePerPage() {
        effectivePerPage = window.innerWidth < 768 ? mobilePerPage : perPage;
    }

    const dispatch = createEventDispatcher();

    onMount(() => {
        calculatePerPage();

        window.addEventListener("resize", calculatePerPage);

        let autoplayId: number | null = null;

        if (autoPlayIntervalForSlider > 0) {
            autoplayId = window.setInterval(() => {
                emblaApi?.scrollNext();
            }, autoPlayIntervalForSlider);
        }

        return () => {
            window.removeEventListener("resize", calculatePerPage);
            if (autoplayId) {
                clearInterval(autoplayId);
            }
        };
    });

    const handleNext = (): void => {
        emblaApi?.scrollNext();
    };

    const handlePrev = (): void => {
        emblaApi?.scrollPrev();
    };

    const handleSelect = (): void => {
        if (emblaApi) {
            currentIndex = emblaApi?.selectedScrollSnap() || 0;
            const shown = (currentIndex + 1) * effectivePerPage;

            if (shown >= posts.length) {
                dispatch("loadMore");
            }
        }
    };
</script>

{#if posts.length}
    <div class="gw-ig-gallery-slide-layout">
        <EmblaSlider
            setApi={(api) => (emblaApi = api)}
            perPage={effectivePerPage}
            {loop}
            controls={true}
            gap={gutter}
            on:select={handleSelect}
        >
            <svelte:fragment slot="slides">
                {#if skeleton}
                    {#each { length: skeletonPostsCount } as _}
                        <div
                            class="gw-ig-gallery-slide-layout__post-wrapper gw-ig-gallery-slide-layout__post-wrapper--skeleton"
                        >
                            <InstagramPostSkeleton square={true} />
                        </div>
                    {/each}
                {:else}
                    {#each posts as post}
                        <div class="gw-ig-gallery-slide-layout__post-wrapper">
                            <InstagramPost
                                {post}
                                square={true}
                                {postModalToken}
                                imageObjectFit="cover"
                                {displayMode}
                                {highQualityEnabled}
                            />
                        </div>
                    {/each}
                {/if}
            </svelte:fragment>

            <svelte:fragment slot="arrows" let:hasNext let:hasPrev>
                <SliderArrows
                    on:nextSlide={handleNext}
                    on:prevSlide={handlePrev}
                    {hasNext}
                    {hasPrev}
                />
            </svelte:fragment>

            <EmblaSliderDots
                {currentIndex}
                slot="dots"
                perPage={effectivePerPage}
                slidesCount={posts.length}
                api={emblaApi || undefined}
                class="gw-ig-gallery-slide-layout__dots"
            >
                <Icon width={5} height={5}>
                    <DotsIcon />
                </Icon>
            </EmblaSliderDots>
        </EmblaSlider>
    </div>
{/if}

<style lang="postcss">
    .gw-ig-gallery-slide-layout {
        position: relative;
        width: 100%;
        margin: 0 auto;

        :global(.gw-slider-arrows .gw-slider-arrows__arrow) {
            padding: 10px;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.5);
            opacity: 0.6;
            color: #fff;

            &:hover {
                opacity: 1;
            }
        }

        @media (max-width: 767px) {
            padding: 0 1rem;
        }
    }

    .gw-ig-gallery-slide-layout__post-wrapper {
        margin: 0 var(--gw-slider-gutter);
    }

    :global(.gw-ig-gallery-slide-layout__dots) {
        color: var(--gw-lightgrey-text-color);
    }

    :global(.gw-ig-gallery-slide-layout__dots .gw-dots__button--active) {
        color: var(--gw-blue-text-color);
    }
</style>
